import React, { useEffect, useState } from 'react';
import { Upload, message, Switch, Button } from 'antd';
import ArrowUpIcon from "../../assets/images/icons/arrow_up.svg";
import { CloseRounded } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { toggleTour } from '../../reducers/tourSlice';
import './UploadMedia.scss';
import * as XLSX from 'xlsx';

const { Dragger } = Upload;

const UploadMedia = ({ handleFileSelect, fileList, fileClear, handelClick, disabled }) => {
    const dispatch = useDispatch();
    const [fileType, setFileType] = useState('.xlsx'); // Default file type
    const [selectedFileName, setSelectedFileName] = useState(null);

    useEffect(() => {
        setSelectedFileName(null)
    }, [fileClear])

    const beforeUpload = (file) => {
        // Define allowed MIME types for .xlsx and .zip files
        const allowedMimeTypes = fileType === '.xlsx'
            ? ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
            : ['application/zip', 'application/x-zip-compressed'];

        // Check if the MIME type is allowed
        const isAllowedType = allowedMimeTypes.includes(file.type);

        if (!isAllowedType) {
            message.error(`Invalid File format. File should be in ${fileType} format`);
            setSelectedFileName(null);  // Clear the file name if the file format is invalid
            handleFileSelect([], fileType);  // Clear fileList
            return Upload.LIST_IGNORE; // Prevent the file from being selected
        }

        // Check if the file size is greater than 150MB
        if ((file.size / 1024 / 1024) > 150) {
            message.error('File must be smaller than 150MB!');
            setSelectedFileName(null);  // Clear the file name if the file is too large
            handleFileSelect([], fileType);  // Clear fileList
            return Upload.LIST_IGNORE; // Prevent the file from being selected
        }

        // Check for special characters in file name (allowed characters: letters, numbers, dashes, and ampersands)
        const validFilenameRegex = fileType == '.xlsx' ? /^[a-zA-Z0-9\-&_ ]+\.xlsx$/ : /^[a-zA-Z0-9\-&_ ]+\.zip$/
        if(!validFilenameRegex.test(file.name)) {
            message.error("File name contains invalid characters.");
            handleFileSelect([], fileType);  // Clear fileList
            return Upload.LIST_IGNORE;
        }

        // Checking for duplicate files by name
        const isDuplicate = fileList.some(existingFile => existingFile.name === file.name);
        if (isDuplicate) {
            setSelectedFileName(null);  // Clear the file name if it's a duplicate
            handleFileSelect([], fileType);  // Clear fileList
            return Upload.LIST_IGNORE; // Prevent the file from being selected
        }

        // If file is an .xlsx file, validate the content using the first few bytes
        if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
            const reader = new FileReader();

            reader.onload = (e) => {
                const data = e.target.result;

                // Check if the file's first 4 bytes match the signature for .xlsx files (ZIP format)
                const header = new Uint8Array(data).slice(0, 4);
                const isXlsx = header[0] === 80 && header[1] === 75 && header[2] === 3 && header[3] === 4; // 'PK\x03\x04' in ASCII

                if (!isXlsx) {
                    message.error('Invalid content. The file is not a valid Excel file.');
                    setSelectedFileName(null);  // Clear the file name if the content is invalid
                    handleFileSelect([], fileType);  // Clear fileList
                    return Upload.LIST_IGNORE; // Prevent the file from being selected
                }

                try {
                    // Read the content of the file as an Excel file
                    const workbook = XLSX.read(data, { type: 'array' });

                    // Ensure the workbook has at least one sheet (basic check)
                    if (workbook.SheetNames.length === 0) {
                        message.error('Invalid content. File must contain at least one sheet.');
                        setSelectedFileName(null);  // Clear the file name if the content is invalid
                        handleFileSelect([], fileType);  // Clear fileList
                        return Upload.LIST_IGNORE; // Prevent the file from being selected
                    }
                } catch (error) {
                    message.error('Invalid Excel file content');
                    setSelectedFileName(null);  // Clear the file name if reading the Excel content fails
                    handleFileSelect([], fileType);  // Clear fileList
                    return Upload.LIST_IGNORE; // Prevent the file from being selected
                }
            };

            reader.readAsArrayBuffer(file);
            // Return false to prevent upload while file is being validated asynchronously
            return false; // Ensures that upload waits until validation is complete
        }

        // If file is a .zip file, validate its MIME type and content
        if (file.type === 'application/zip' || file.type === 'application/x-zip-compressed') {
            const reader = new FileReader();

            reader.onload = (e) => {
                const data = e.target.result;

                // Check if the file's first 4 bytes match the signature for a .zip file (ZIP format)
                const header = new Uint8Array(data).slice(0, 4);
                const isZip = header[0] === 80 && header[1] === 75 && header[2] === 3 && header[3] === 4; // 'PK\x03\x04' in ASCII

                if (!isZip) {
                    message.error('Invalid content. The file is not a valid ZIP file.');
                    setSelectedFileName(null);  // Clear the file name if the content is invalid
                    handleFileSelect([], fileType);  // Clear fileList
                    return Upload.LIST_IGNORE; // Prevent the file from being selected
                }

                // Additional zip validation (if needed) can be added here
            };

            reader.readAsArrayBuffer(file);
            return false; // Prevent upload while validating zip content
        }

        // If all checks pass, return true to allow the upload
        return true;
    };

    const handleFileChange = (file) => {
        // file.fileList.map(item => item.originFileObj ? item.originFileObj : item)
        const selectedFile = file.fileList.map(item => item.originFileObj ? item.originFileObj : item)
        handleFileSelect(selectedFile, fileType); // Pass fileType to parent component
        if (selectedFile) {
            const maxLength = 20; // Set your desired maximum length
            const truncatedFileName = selectedFile[0].name.length > maxLength
                ? selectedFile[0].name.substring(0, maxLength) + '...'
                : selectedFile[0].name;
            setSelectedFileName(truncatedFileName);
        } else {
            setSelectedFileName(null); // Reset selected file name if no file is selected
        }
    };

    const customRequest = ({ file, onSuccess }) => {
        if (file && file.name) {
            onSuccess("ok");
        }
    }

    const onRemove = () => {
        handleFileSelect([], fileType); // Pass fileType to parent component
        setSelectedFileName(null); // Reset selected file name
    }

    const handleClickOnDragger = () => {
        dispatch(toggleTour({ show: false }));
    }

    const handleFileTypeChange = (checked) => {
        const newFileType = checked ? '.zip' : '.xlsx';
        setFileType(newFileType);
        handleFileSelect([], newFileType); // Reset fileList and pass new fileType to parent component
    }

    return (
        <div className='wrapper'>
            <div className="upload-container">
                {/* Left part for file type toggle */}
                <div className="file-type-toggle">
                    <div className='title1'>File type</div>
                    <Switch
                        checkedChildren="zip"
                        unCheckedChildren="xlsx"
                        defaultChecked={false}
                        onChange={handleFileTypeChange}
                    />
                    <span></span>
                </div>

                {/* Vertical line */}
                <div className="vertical-line"></div>

                {/* Right part for upload button */}
                <Dragger
                    className='pcvc-upload'
                    fileList={fileList}
                    multiple={false}
                    beforeUpload={beforeUpload}
                    onChange={handleFileChange}
                    customRequest={customRequest}
                    showUploadList={false}
                    maxCount={1}
                    onClick={handleClickOnDragger}
                    disabled={fileList.length > 0}
                >
                    {selectedFileName ? (
                        <div className="selected-file">
                            <div className="selected-file-name">{selectedFileName}</div>
                            <CloseRounded className="remove" onClick={onRemove} />
                        </div>
                    ) : (
                            <div className='upload-action'>
                                <div className='title'>Drag and drop here or</div>
                                <button className="btn-outlined">
                                    <div className='btn-outlined-content'>
                                        <div className='text'>Upload documents </div>
                                        <img width={29} src={ArrowUpIcon} alt="Upload Icon" />
                                    </div>
                                </button>
                            </div>
                        )}

                </Dragger>
            </div>
            <div className="actions">
                <Button type="primary" onClick={handelClick} disabled={disabled}>
                    Submit
                     </Button>
            </div>
        </div>
    );
};

export default UploadMedia;
